import {
  Routes,
  Route,
  RoutesProps,
  useNavigate,
  useLocation,
} from "react-router-dom";

import React, { Suspense, useEffect } from "react";
import { useState } from "react";
import ProtectedRoute from "./ProtectedRoute";
import PublicRoute from "./PublicRoute";
import Main from "../components/Main";
import { CategoryProvider } from "../context/CategoryContext";

const LoginFallback = () => (
  <div className="login-page">
    <div className="ambient-glow ambient-1"></div>
    <div className="ambient-glow ambient-2"></div>
  </div>
);

const SetupProfileFallback = () => (
  <div style={{ backgroundColor: "#F4F4F5", height: "100vh", width: "100%" }} />
);

const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const tableId = localStorage.getItem("table_id");
    const isScanRoute = location.pathname.startsWith("/scan");
    if (!tableId && !isScanRoute) {
      navigate("/scan", { replace: true });
    }
  }, [location.pathname, navigate]);

  interface ProtectedRouteProps extends RoutesProps {
    authenticationPath: string;
  }
  const defaultProtectedRouteProps: Omit<ProtectedRouteProps, "outlet"> = {
    authenticationPath: "/login",
  };

  // helper to ensure the lazy() factory resolves to a React component type
  const lazyLoad = <T extends React.ComponentType<any>>(factory: () => Promise<{ default: T }>) =>
    React.lazy(factory);
  const LoaderScreen = lazyLoad(() => import("../pages/scanner"));
  const InteractiveScanner = lazyLoad(() => import("../pages/interactive-scanner"));
  const Login = lazyLoad(() => import("../components/Login/Login"));
  const OtpSuccess = lazyLoad(() => import("../components/Login/Success"));
  const SetupProfile = lazyLoad(() => import("../components/Login/SetupProfile"));

  const Home = lazyLoad(() => import("../pages/Home/Home"));
  const MenuListing = lazyLoad(() => import("../pages/Home/MenuItemListing"));
  const Cart = lazyLoad(() => import("../pages/Cart/Cart"));
  const AllOffer = lazyLoad(() => import("../pages/Offers/AllOffer"));
  const SplittBill = lazyLoad(() => import("../pages/Cart/SplittBill"));
  const PaymentMethod = lazyLoad(() => import("../pages/Cart/PaymentMethod"));
  const Rewards = lazyLoad(() => import("../pages/Offers/Rewards"));
  const Orders = lazyLoad(() => import("../pages/Orders/Orders"));
  const OrderDetails = lazyLoad(() => import("../pages/Orders/OrderDetails"));
  const BillDetail = lazyLoad(() => import("../pages/Orders/BillDetails"));
  const Receipt = lazyLoad(() => import("../pages/Orders/Receipt"));
  const PastOrderBillDetails = lazyLoad(() => import("../pages/Orders/PastOrderBillDetails"));
  const WriteReview = lazyLoad(() => import("../pages/Orders/WriteReview"));
  const Profile = lazyLoad(() => import("../pages/Profile/Profile"));
  const EditProfile = lazyLoad(() => import("../pages/Profile/EditProfile"));
  const Feedback = lazyLoad(() => import("../pages/Profile/Feedback"));
  const FeedbackSuccess = lazyLoad(() => import("../pages/Orders/FeedbackSuccess"));
  const Updates = lazyLoad(() => import("../pages/Updates/Updates"));
  const OrderSuccess = lazyLoad(() => import("../pages/Cart/Success"));
  const Notification = lazyLoad(() => import("../pages/Notifications/Notifications"));


  return (
    <>
      <div id="main-wraper">
        <Routes>

          <Route path="/scan/:uuid" element={<Suspense fallback={<LoginFallback />}> <LoaderScreen /> </Suspense>} />
          <Route path="/scan" element={<Suspense fallback={<LoginFallback />}> <InteractiveScanner /> </Suspense>} />
          <Route element={<PublicRoute />}>
            <Route path="/login" element={<Suspense fallback={<LoginFallback />}> <Login /> </Suspense>} />
          </Route>
          <Route path="/otp-success" element={<Suspense fallback={<LoginFallback />}> <OtpSuccess /> </Suspense>} />
          <Route path="/setup-profile" element={<Suspense fallback={<SetupProfileFallback />}> <SetupProfile /> </Suspense>} />
          <Route path="/" element={<ProtectedRoute {...defaultProtectedRouteProps} outlet={<Main />} />}>
            <Route path="/home" element={<Suspense fallback={<></>}> <Home /> </Suspense>} />
            <Route path="/menu-listing" element={<Suspense fallback={<></>}> <CategoryProvider><MenuListing /></CategoryProvider> </Suspense>} />
            <Route path="/menu-listing/:id" element={<Suspense fallback={<></>}> <CategoryProvider><MenuListing /></CategoryProvider> </Suspense>} />
            <Route path="/cart" element={<Suspense fallback={<></>}> <Cart /> </Suspense>} />
            <Route path="/all-offers" element={<Suspense fallback={<></>}> <AllOffer /> </Suspense>} />
            <Route path="/splitt-bill" element={<Suspense fallback={<></>}> <SplittBill /> </Suspense>} />
            <Route path="/payment-method" element={<Suspense fallback={<></>}> <PaymentMethod /> </Suspense>} />
            <Route path="/rewards" element={<Suspense fallback={<></>}> <Rewards /> </Suspense>} />
            <Route path="/orders" element={<Suspense fallback={<></>}> <Orders /> </Suspense>} />
            <Route path="/bill-details" element={<Suspense fallback={<></>}> <BillDetail /> </Suspense>} />
            <Route path="/receipt" element={<Suspense fallback={<></>}> <Receipt /> </Suspense>} />
            <Route path="/past-order-bill-details" element={<Suspense fallback={<></>}> <PastOrderBillDetails /> </Suspense>} />
            <Route path="/write-review" element={<Suspense fallback={<></>}> <WriteReview /> </Suspense>} />
            <Route path="/profile" element={<Suspense fallback={<></>}> <Profile /> </Suspense>} />
            <Route path="/edit-profile" element={<Suspense fallback={<></>}> <EditProfile /> </Suspense>} />
            <Route path="/feedback" element={<Suspense fallback={<></>}> <Feedback /> </Suspense>} />
            <Route path="/feedback-success" element={<Suspense fallback={<></>}> <FeedbackSuccess /> </Suspense>} />
            <Route path="/updates" element={<Suspense fallback={<></>}> <Updates /> </Suspense>} />
            <Route path="/order-success" element={<Suspense fallback={<></>}> <OrderSuccess /> </Suspense>} />
            <Route path="/order-details" element={<Suspense fallback={<></>}> <OrderDetails /> </Suspense>} />
            <Route path="/notifications" element={<Suspense fallback={<></>}> <Notification /> </Suspense>} />
          </Route>
        </Routes >
      </div >
    </>
  );
};

export default Navigation;
